'use client';

import { NavigableButton } from '@carvana/ds-button';
import Card from '@carvana/ds-card';
import Carousel from '@carvana/ds-carousel';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import cx from 'classnames';
import { useContext } from 'react';
import { INTERACTION_TO_ACTION } from '~/constants/common/CarouselAnalytics';
import topPicks from '~/constants/dealership/testDriveCenter/topPicks';
import optimizeImage from '~/utilities/optimizeImage';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';

function Header() {
  return (
    <div className="flex flex-col gap-y-lg md:gap-y-xl">
      <h2 className="text-text-strong t-heading-xl md:t-heading-2xl">
        Top picks
      </h2>
      <p className="text-text-default t-body-sm md:t-body-md">
        Discover the models our customers keep coming back for—powerful,
        versatile, and built to impress.
      </p>
    </div>
  );
}

export default function TestDriveCenterTopPicks() {
  const { trackEvent } = useContext(AnalyticsContext);

  return (
    <div className="bg-canvas-muted py-6xl md:py-7xl">
      <Carousel
        ariaLabel="Top picks"
        classes={{
          slide: cx(
            'pl-2xl md:pl-3xl lg:pl-4xl',
            'last:pr-2xl md:last:pr-3xl lg:last:pr-4xl'
          ),
          slides: 'gap-0',
          topSection: 'px-2xl md:px-3xl lg:px-4xl',
          wrapper: 'mx-auto max-w-[calc(1272px+var(--spacing-4xl)*2)]'
        }}
        headerContent={<Header />}
        hideScrollBar
        hidePagination
        trackEvent={event => {
          trackEvent('Top picks', {
            ...event,
            action: INTERACTION_TO_ACTION[event.interactionType]
          });
        }}
        slideByVisible
      >
        {topPicks.map(vehicle => (
          <Card
            key={vehicle.name}
            media={
              <img
                src={optimizeImage(vehicle.image, {
                  crop: '16:9',
                  format: 'auto',
                  optimize: 'medium'
                })}
                alt={vehicle.name}
                className="aspect-video w-full object-cover"
              />
            }
            className="flex h-full w-[288px] flex-col"
            classes={{
              content: 'flex flex-1 flex-col gap-y-2xl',
              media: 'flex'
            }}
          >
            <div className="flex flex-1 flex-col gap-y-lg">
              <h3 className="text-text-strong t-heading-lg">{vehicle.name}</h3>
              <p className="text-text-default t-body-sm">{vehicle.blurb}</p>
            </div>
            <NavigableButton
              variant="secondary"
              size="md"
              width="fit-content"
              href={`/cars/filters?cvnaid=${vehicle.cvnaId}`}
              data-adobe-id={`view-inventory-${toLowerKebabCase(vehicle.name)}`}
              data-analytics-id={`Top picks - View Inventory - ${vehicle.name}`}
              data-analytics-attributes={JSON.stringify({
                item: vehicle.name
              })}
              data-testid={`test-drive-center-view-inventory-${toLowerKebabCase(
                vehicle.name
              )}`}
            >
              View Inventory
            </NavigableButton>
          </Card>
        ))}
      </Carousel>
    </div>
  );
}
